import React, { forwardRef } from 'react';

import type {
  DynamicBlockElement,
  SelBoundaryNode,
  TableCellElement,
  TableElement,
} from '@aline/common';
import {
  isDynamicBlockElement,
  isParagraphElement,
  isTableCellElement,
  isTableElement,
  nodeIsSelBoundary,
} from '@aline/common';
import { Editor } from 'slate';
import { BaseMenuOption } from 'src/editor/toolbars/menus/BaseMenuOption';
import type { CommonMenuItemProps } from 'src/editor/toolbars/menus/types';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

interface InsertParagraphMenuItemProps extends CommonMenuItemProps {
  direction: 'above' | 'below';
}

export const InsertParagraphMenuItem = forwardRef(function InsertParagraphMenuItem(
  props: InsertParagraphMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element | null {
  const { closeRootMenu, direction, ...rest } = props;
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const entryAbove = Editor.above<TableElement | DynamicBlockElement>(editor, {
    match: n => isTableElement(n) || isDynamicBlockElement(n),
    voids: true,
  });
  const label = `Insert paragraph ${direction}`;

  function insertParagraph(): void {
    if (!editingMode || !currentUserId || !entryAbove) return closeRootMenu();

    const [, path] = entryAbove;
    AuthorizedTransforms.insertParagraph(editor, {
      at: path,
      authorId: currentUserId,
      direction,
      editingMode,
    });
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  // Selection is not inside a table or dynamic block.  Don't show the menu item.
  if (!entryAbove) return null;

  return <BaseMenuOption ref={ref} label={label} onClick={insertParagraph} {...rest} />;
});

export const RemoveParagraphMenuItem = forwardRef(function RemoveParagraphMenuItem(
  props: CommonMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element | null {
  const { closeRootMenu, disabled, ...rest } = props;
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const ancestorEntry = Editor.above<SelBoundaryNode | DynamicBlockElement | TableCellElement>(
    editor,
    {
      match: n => nodeIsSelBoundary(n) || isDynamicBlockElement(n) || isTableCellElement(n),
      voids: true,
    }
  );
  const paragraphEntryAboveSel = Editor.above(editor, { match: isParagraphElement });

  function removeParagraph(): void {
    if (!editingMode || !currentUserId) return closeRootMenu();

    AuthorizedTransforms.removeParagraphAboveSelection(editor);
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  if (!ancestorEntry) return null;

  const [ancestor] = ancestorEntry;
  const isLastBlockInAncestor = ancestor.children.length === 1;

  return (
    <BaseMenuOption
      ref={ref}
      disabled={disabled || isLastBlockInAncestor || !paragraphEntryAboveSel}
      label="Remove paragraph"
      onClick={removeParagraph}
      {...rest}
    />
  );
});
